import {useEffect,useRef,useState,type FormEvent} from 'react';
import {ApiError,apiFetch,isAbortError} from './account/api';
import {AVATAR_ACCEPT,avatarFileToDataUrl,describePasswordError,normalizeNickname,passwordByteLength,setFirstPassword,updateCurrentUserProfile} from './account/data';
import {useSession} from './account/session';

/** The first password remains mandatory before granting a desktop session. */
export function FirstPasswordSetup() {
 const {user,version,updateUser}=useSession();
 const [password,setPassword]=useState(''),[confirm,setConfirm]=useState('');
 const [busy,setBusy]=useState(false),[error,setError]=useState('');
 const controller=useRef<AbortController|null>(null);
 const inFlight=useRef(false);
 useEffect(()=>()=>controller.current?.abort(),[]);
 const submit=async(event:FormEvent)=>{
  event.preventDefault();if(!user||inFlight.current)return;
  if(passwordByteLength(password)<8||passwordByteLength(password)>72){setError('密码长度需为 8–72 字节。');return}
  if(password!==confirm){setError('两次输入的密码不一致。');return}
  const abort=new AbortController();controller.current=abort;
  inFlight.current=true;setBusy(true);setError('');
  try{await setFirstPassword(password,abort.signal,version);if(!abort.signal.aborted)updateUser({id:user.id,has_password:true})}
  catch(error){if(!abort.signal.aborted&&!isAbortError(error))setError(describePasswordError(error).message)}
  finally{inFlight.current=false;if(!abort.signal.aborted)setBusy(false)}
 };
 return <form className="auth-form desktop-auth-form" onSubmit={submit}>
  <h2>设置登录密码</h2>
  <p>先为账号设置密码，再继续授权客户端。</p>
  <div className="auth-field"><label htmlFor="setup-password">新密码</label><input id="setup-password" type="password" autoComplete="new-password" value={password} onChange={event=>setPassword(event.target.value)} required/></div>
  <div className="auth-field"><label htmlFor="setup-confirm">确认密码</label><input id="setup-confirm" type="password" autoComplete="new-password" value={confirm} onChange={event=>setConfirm(event.target.value)} required/></div>
  {error&&<p className="auth-error" role="alert">{error}</p>}
  <button className="auth-primary" disabled={busy}>{busy?'正在保存…':'保存密码并继续'}</button>
 </form>;
}

export function FirstProfileSetup({onDone,wechatProfile}:{onDone:()=>void;wechatProfile?:{nickname?:string;avatarUrl?:string}}) {
 const {user,version,updateUser}=useSession();
 const [nickname,setNickname]=useState(user?.phone?.slice(-4)||'新用户');
 const [avatar,setAvatar]=useState<string|null>(null);
 const [busy,setBusy]=useState(false),[error,setError]=useState('');
 const controller=useRef<AbortController|null>(null),reading=useRef(0),inFlight=useRef(false);
 let wechatNickname:string|undefined;
 try{if(wechatProfile?.nickname)wechatNickname=normalizeNickname(wechatProfile.nickname)}catch{/* Invalid provider text is not offered. */}
 const wechatAvatar=wechatProfile?.avatarUrl&&wechatProfile.avatarUrl.length<=900_000&&/^data:image\/(?:png|jpeg|jpg|gif|webp);base64,[A-Za-z0-9+/]+={0,2}$/.test(wechatProfile.avatarUrl)?wechatProfile.avatarUrl:null;
 useEffect(()=>()=>{controller.current?.abort();reading.current++},[]);
 const upload=async(file?:File)=>{
  if(!file)return;const generation=++reading.current;setError('');
  try{const value=await avatarFileToDataUrl(file);if(generation===reading.current)setAvatar(value)}
  catch(error){if(generation===reading.current)setError(error instanceof Error?error.message:'无法读取头像，请重新选择。')}
 };
 const submit=async(event:FormEvent)=>{
  event.preventDefault();if(!user||inFlight.current)return;
  let name:string;try{name=normalizeNickname(nickname)}catch(error){setError((error as Error).message);return}
  const abort=new AbortController();controller.current=abort;inFlight.current=true;setBusy(true);setError('');
  try{
   const updated=await updateCurrentUserProfile({nick_name:name,...(avatar?{avatar_url:avatar}:{})},abort.signal,version);
   if(!abort.signal.aborted){updateUser(updated);onDone()}
  }catch(error){if(!abort.signal.aborted&&!isAbortError(error))setError('资料暂未保存，请重试或跳过。')}
  finally{inFlight.current=false;if(!abort.signal.aborted)setBusy(false)}
 };
 return <form className="auth-form desktop-auth-form" onSubmit={submit}>
  <h2>完善个人资料</h2><p>可以现在设置，也可以稍后在个人中心修改。</p>
  {avatar&&<img className="desktop-auth-avatar" src={avatar} alt="所选头像"/>}
  <div className="auth-field"><label htmlFor="setup-avatar">选择头像（选填）</label><input id="setup-avatar" type="file" accept={AVATAR_ACCEPT} disabled={busy} onChange={event=>void upload(event.target.files?.[0])}/></div>
  <div className="auth-field"><label htmlFor="setup-nickname">昵称</label><input id="setup-nickname" value={nickname} onChange={event=>setNickname(event.target.value)} maxLength={64} required/></div>
  {wechatNickname&&<button className="auth-outline" type="button" disabled={busy} onClick={()=>setNickname(wechatNickname!)}>使用微信昵称</button>}
  {wechatAvatar&&<button className="auth-outline" type="button" disabled={busy} onClick={()=>{reading.current++;setAvatar(wechatAvatar)}}>使用微信头像</button>}
  {error&&<p className="auth-error" role="alert">{error}</p>}
  <button className="auth-primary" disabled={busy}>{busy?'正在保存…':'保存资料并继续'}</button>
  <button className="auth-outline" type="button" disabled={busy} onClick={onDone}>暂时跳过</button>
 </form>;
}

export function InvitationSetup({onAuthorize,authorizing}:{onAuthorize:()=>Promise<void>;authorizing:boolean}) {
 const {version}=useSession();
 const [enabled,setEnabled]=useState(false),[loadError,setLoadError]=useState(false),[retry,setRetry]=useState(0);
 const [code,setCode]=useState(''),[bound,setBound]=useState(false),[busy,setBusy]=useState(false),[error,setError]=useState('');
 const controller=useRef<AbortController|null>(null),inFlight=useRef(false);
 useEffect(()=>{
  const abort=new AbortController();setLoadError(false);
  void apiFetch<{enabled:boolean;campaign:unknown}>('/v1/public/invitation-campaign',{signal:abort.signal})
   .then(result=>{if(!abort.signal.aborted)setEnabled(result.enabled&&result.campaign!==null)})
   .catch(()=>{if(!abort.signal.aborted)setLoadError(true)});
  return()=>abort.abort();
 },[retry]);
 useEffect(()=>()=>controller.current?.abort(),[]);
 const bind=async():Promise<boolean>=>{
  if(inFlight.current)return false;
  if(!enabled||bound||!code.trim())return true;
  inFlight.current=true;setBusy(true);setError('');
  const abort=new AbortController();controller.current=abort;
  try{await apiFetch('/v1/invitation/bind',{auth:true,body:{code:code.trim()},signal:abort.signal,expectedSessionVersion:version});if(abort.signal.aborted)return false;setBound(true);return true}
  catch(error){if(!abort.signal.aborted&&!isAbortError(error))setError(error instanceof ApiError&&error.status===409?'当前账号已绑定过邀请人或暂时无法绑定，可继续登录。':'邀请码暂未绑定，请检查后重试，或选择不绑定。');return false}
  finally{inFlight.current=false;if(!abort.signal.aborted)setBusy(false)}
 };
 const proceed=async()=>{if(!authorizing&&await bind())await onAuthorize()};
 return <>
 {loadError&&<div className="desktop-auth-optional"><p>暂时无法读取邀请活动，可继续登录。</p><button className="auth-outline" onClick={()=>setRetry(value=>value+1)}>重试读取活动</button></div>}
 {enabled&&<div className="desktop-auth-optional">
  {bound?<p role="status">邀请码已绑定</p>:<><div className="auth-field"><label htmlFor="desktop-invite">活动邀请码（选填）</label><input id="desktop-invite" autoComplete="off" value={code} onChange={event=>{setCode(event.target.value);setError('')}} maxLength={32}/></div>
   {code.trim()&&<button className="auth-outline" disabled={busy||authorizing} onClick={()=>void bind()}>{busy?'正在绑定…':'绑定邀请码'}</button>}
   {error&&<p role="alert" className="auth-error">{error}</p>}
  </>}
 </div>}
 <button className="auth-primary" disabled={busy||authorizing} onClick={()=>void proceed()}>{busy?'正在绑定…':authorizing?'正在授权…':'继续并授权'}</button>
 {error&&<button className="auth-outline" disabled={busy||authorizing} onClick={()=>{setCode('');setError('');void onAuthorize()}}>不绑定，继续授权</button>}
 </>;
}
